// The Northern Reach in motion, layered into the Square's ambient passes
// (ambient.js calls each of these from its own): the northern lights after
// dark, snow drifting down, a whale surfacing out at sea, icebergs turning
// slowly, buoys bobbing along the ferry lanes, waves on the shingle, steam off
// the sauna and its hot spring, smoke from the hall and the ice hut, flags on
// the world port's poles, pigeons circling their loft, reindeer grazing the
// snowfields, bottles drifting in to Bottle cove, and a float on the fishing
// hole. Everything is culled to the view; with reduced motion it all holds
// still (and the flags still fly, just without flapping).
import { rng, noise2, line, withAlpha } from "./paint"

const TAU = Math.PI * 2

export class NorthAmbient {
    constructor(world, { seed = 1, reduceMotion = false, glow } = {}) {
        this.world = world
        this.reach = world.reach
        this.active = !!(world.reach && world.reach.active)
        this.reduce = reduceMotion
        this.glow = glow
        this.r = rng(seed + 900)
        this.n = noise2(seed + 901)
        this.t = 0
        this.view = { x: 0, y: 0, w: world.W, h: world.H }
        this.steam = []
        this.smoke = []
        if (!this.active) return
        const a = world.anchors
        const r = this.r
        const inSea = (x, y) => world.inSea(x, y)
        // icebergs: in the fjord's mouth and out in the northern sea
        this.bergs = []
        for (let i = 0; i < 60 && this.bergs.length < 9; i++) {
            const x = 5400 + r() * 2500, y = 2400 + r() * 2400, s = 18 + r() * 34
            if (!inSea(x, y) || this.reach.islandAt(x, y, -160) || this.reach.onFerry(x, y, 120)) continue
            this.bergs.push({ x, y, s, phase: r() * TAU, sides: 6 + Math.floor(r() * 3), seed: Math.floor(r() * 1e6), spin: (r() - 0.5) * 0.02 })
        }
        // buoys along each ferry lane
        this.buoys = []
        this.reach.lanes.forEach((lane) => {
            for (let i = 18; i < lane.length - 18; i += 20) {
                const p = lane[i]
                if (inSea(p.x, p.y)) this.buoys.push({ x: p.x + p.ny * 30 * (i % 40 ? 1 : -1), y: p.y - p.nx * 30 * (i % 40 ? 1 : -1), phase: r() * TAU, red: (i / 20) % 2 === 0 })
            }
        })
        // the whale's waters, and when it next comes up
        this.whale = null
        this.nextWhale = 4 + r() * 6
        // the reindeer herd, between the cairn and the glacier
        const range = { x: 6180, y: 1380, rx: 260, ry: 170 }
        this.range = range
        this.deer = Array.from({ length: 5 }, (_, i) => ({ x: range.x + (r() - 0.5) * range.rx, y: range.y + (r() - 0.5) * range.ry, tx: range.x, ty: range.y, graze: r() * 4, facing: r() < 0.5 ? -1 : 1, bob: r() * TAU, calf: i === 4 }))
        // pigeons round the loft; now and then a few fly off and come back
        this.pigeons = a.loft ? Array.from({ length: 8 }, (_, i) => ({ a: r() * TAU, rad: 70 + r() * 70, h: 40 + r() * 50, speed: 0.9 + r() * 0.6, phase: r() * TAU, away: 0, dir: r() * TAU, i })) : []
        this.nextFlight = 6 + r() * 8
        // bottles drifting in toward the cove
        this.bottles = (a.driftBottles || []).map((b) => ({ ...b, hx: b.x, hy: b.y, phase: r() * TAU, age: r() * 30 }))
        // snowflakes, placed across the view and wrapped as it moves
        this.flakes = Array.from({ length: 150 }, () => ({ u: r(), v: r(), s: 0.8 + r() * 1.8, speed: 14 + r() * 16, sway: r() * TAU }))
        this.curtain = makeCurtain()
        this.nextBob = 0
        this.ripples = []
    }

    setView(view) { this.view = view }
    #visible(x, y, pad = 120) {
        const v = this.view
        return x > v.x - pad && x < v.x + v.w + pad && y > v.y - pad && y < v.y + v.h + pad
    }

    update(dt, { night, drift }) {
        if (!this.active) return
        this.t += Math.max(0, dt)
        if (this.reduce) return
        const r = this.r, a = this.world.anchors
        const advance = (list, fn) => { for (let i = list.length - 1; i >= 0; i--) { const p = list[i]; p.age += dt; if (p.age >= p.life) list.splice(i, 1); else fn(p) } }

        // steam off the sauna's chimney and its hot spring; smoke from the hall
        // and the ice hut
        if (a.saunaSteam && this.#visible(a.saunaSteam[0].x, a.saunaSteam[0].y, 500)) {
            a.saunaSteam.forEach((s, i) => { if (r() < dt * (i ? 2.2 : 1.4)) this.steam.push({ x: s.x + (r() - 0.5) * 20, y: s.y, vx: drift * 0.2 + (r() - 0.5) * 5, vy: -9 - r() * 6, age: 0, life: 3.4 + r(), s: i ? 7 : 4 }) })
        }
        ;[a.guildSmoke, a.hutSmoke].forEach((c) => { if (c && this.#visible(c.x, c.y, 500) && r() < dt * 1.5) this.smoke.push({ x: c.x + (r() - 0.5) * 4, y: c.y, vx: drift * 0.3 + (r() - 0.5) * 5, vy: -15 - r() * 9, age: 0, life: 3.6, s: 4 }) })
        advance(this.steam, (p) => { p.x += (p.vx + drift * 0.4 * (p.age / p.life)) * dt; p.y += p.vy * dt; p.vy *= 0.994 })
        advance(this.smoke, (p) => { p.x += (p.vx + drift * 0.6 * (p.age / p.life)) * dt; p.y += p.vy * dt; p.vy *= 0.995 })

        // the whale: up for a few breaths, then the flukes, then gone a while
        this.nextWhale -= dt
        if (!this.whale && this.nextWhale <= 0) {
            for (let k = 0; k < 20; k++) {
                const x = 6300 + r() * 1500, y = 3200 + r() * 1500
                if (!this.world.inSea(x, y) || this.reach.islandAt(x, y, -200) || this.reach.onFerry(x, y, 160)) continue
                this.whale = { x, y, dir: r() < 0.5 ? -1 : 1, age: 0, life: 11 }
                break
            }
            this.nextWhale = 16 + r() * 18
        }
        if (this.whale) {
            const w = this.whale
            w.age += dt
            w.x += w.dir * 9 * dt
            if (w.age >= w.life) this.whale = null
        }

        // reindeer amble between patches of lichen, and graze
        this.deer.forEach((d) => {
            d.bob += dt * 3
            if (d.graze > 0) { d.graze -= dt; return }
            const dx = d.tx - d.x, dy = d.ty - d.y, dist = Math.hypot(dx, dy)
            if (dist < 2) {
                const ang = r() * TAU, k = Math.sqrt(r())
                d.tx = this.range.x + Math.cos(ang) * this.range.rx * k; d.ty = this.range.y + Math.sin(ang) * this.range.ry * k
                d.graze = 3 + r() * 7
                return
            }
            d.facing = dx < 0 ? -1 : 1
            d.x += (dx / dist) * 16 * dt; d.y += (dy / dist) * 16 * dt
        })

        // pigeons wheel round the loft; a few head off and come home
        this.nextFlight -= dt
        if (this.nextFlight <= 0 && this.pigeons.length) {
            const dir = r() * TAU
            this.pigeons.filter((_, i) => i % 3 === Math.floor(r() * 3)).forEach((p) => { p.away = 0.01; p.dir = dir + (r() - 0.5) * 0.3 })
            this.nextFlight = 10 + r() * 14
        }
        this.pigeons.forEach((p) => {
            p.a += p.speed * dt
            p.phase += dt * 14
            if (p.away > 0) { p.away += dt; if (p.away > 9) p.away = 0 }
        })

        // bottles bob in on the tide, and a new one sets out when one lands
        this.bottles.forEach((b) => {
            b.age += dt
            const k = (b.age % 40) / 40
            b.x = b.hx - k * 40
            b.y = b.hy - k * 110
        })

        // the float on the fishing hole: a nibble now and then
        this.nextBob -= dt
        if (a.fishingHole && this.nextBob <= 0) {
            this.ripples.push({ x: a.fishingHole.x, y: a.fishingHole.y, age: 0, life: 1.6 })
            this.nextBob = 3 + r() * 6
        }
        advance(this.ripples, () => {})

        this.bergs.forEach((b) => { b.phase += dt * 0.08 })
    }

    // --- on the water ----------------------------------------------------------------

    drawWater(ctx, pal) {
        if (!this.active) return
        this.#waves(ctx, pal)
        const t = this.reduce ? 0 : this.t
        // icebergs, with the pale green of their underwater shelf
        this.bergs.forEach((b) => {
            const x = b.x + Math.sin(b.phase) * 30, y = b.y + Math.cos(b.phase * 0.8) * 16
            if (!this.#visible(x, y, b.s * 2)) return
            ctx.save()
            ctx.translate(x, y)
            ctx.fillStyle = withAlpha(pal.night ? "#2E6A70" : "#9FE0D8", 0.55)
            ctx.beginPath(); ctx.ellipse(4, 6, b.s * 1.5, b.s * 0.75, 0, 0, TAU); ctx.fill()
            const rr = rng(b.seed), pts = []
            for (let i = 0; i < b.sides; i++) { const ang = (i / b.sides) * TAU + b.phase * 0.5, k = 0.7 + rr() * 0.35; pts.push([Math.cos(ang) * b.s * k, Math.sin(ang) * b.s * 0.55 * k - b.s * 0.25]) }
            ctx.beginPath(); pts.forEach(([px, py], i) => (i ? ctx.lineTo(px, py) : ctx.moveTo(px, py))); ctx.closePath()
            ctx.fillStyle = pal.snowLight; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.stroke()
            ctx.save(); ctx.clip()
            ctx.fillStyle = withAlpha(pal.iceDeep, 0.9); ctx.beginPath(); ctx.ellipse(b.s * 0.4, 0, b.s * 0.7, b.s * 0.5, 0, 0, TAU); ctx.fill()
            ctx.restore()
            ctx.strokeStyle = withAlpha(pal.foam, 0.7); ctx.lineWidth = 1.2
            ctx.beginPath(); ctx.ellipse(0, b.s * 0.2, b.s * 1.1, b.s * 0.35, 0, 0.2, Math.PI - 0.2); ctx.stroke()
            ctx.restore()
        })
        // buoys along the ferry lanes
        this.buoys.forEach((b) => {
            if (!this.#visible(b.x, b.y, 30)) return
            const bob = Math.sin(t * 2 + b.phase) * 1.8, lean = Math.sin(t * 1.4 + b.phase) * 0.18
            ctx.save(); ctx.translate(b.x, b.y + bob); ctx.rotate(lean)
            ctx.strokeStyle = withAlpha(pal.foam, 0.6); ctx.lineWidth = 1.2
            ctx.beginPath(); ctx.ellipse(0, 3, 9, 3.2, 0, 0, TAU); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(-5, 2); ctx.lineTo(-3, -12); ctx.lineTo(3, -12); ctx.lineTo(5, 2); ctx.closePath()
            ctx.fillStyle = b.red ? "#D2544F" : "#FAF7EF"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
            ctx.fillStyle = b.red ? "#FAF7EF" : "#D2544F"; ctx.fillRect(-4, -6, 8, 3)
            ctx.restore()
        })
        // the whale
        if (this.whale && this.#visible(this.whale.x, this.whale.y, 200)) this.#whale(ctx, pal, this.whale)
        // bottles bobbing in toward the cove
        this.bottles.forEach((b) => {
            if (!this.#visible(b.x, b.y, 30)) return
            const bob = Math.sin(t * 1.8 + b.phase) * 1.5
            ctx.save(); ctx.translate(b.x, b.y + bob); ctx.rotate(0.6 + Math.sin(t + b.phase) * 0.2)
            ctx.strokeStyle = withAlpha(pal.foam, 0.6); ctx.lineWidth = 1
            ctx.beginPath(); ctx.ellipse(0, 2, 10, 3, 0, 0, TAU); ctx.stroke()
            ctx.fillStyle = withAlpha("#6FA86A", 0.9); ctx.fillRect(-7, -2.5, 11, 5); ctx.fillRect(4, -1.3, 4, 2.6)
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 0.8; ctx.strokeRect(-7, -2.5, 11, 5)
            ctx.restore()
        })
        // the float on the fishing hole, and the rings when something nibbles
        const hole = this.world.anchors.fishingHole
        if (hole && this.#visible(hole.x, hole.y, 40)) {
            ctx.save()
            this.ripples.forEach((p) => {
                const k = p.age / p.life
                ctx.globalAlpha = (1 - k) * 0.8; ctx.strokeStyle = pal.foam; ctx.lineWidth = 1
                ctx.beginPath(); ctx.ellipse(p.x, p.y, 3 + k * 7, (3 + k * 7) * 0.45, 0, 0, TAU); ctx.stroke()
            })
            ctx.globalAlpha = 1
            const dip = this.ripples.some((p) => p.age < 0.4) ? 1.6 : Math.sin(t * 2.5) * 0.5
            ctx.fillStyle = "#D2544F"; ctx.beginPath(); ctx.arc(hole.x + 2, hole.y + dip - 1, 2.4, Math.PI, 0); ctx.fill()
            ctx.fillStyle = "#FAF7EF"; ctx.beginPath(); ctx.arc(hole.x + 2, hole.y + dip - 1, 2.4, 0, Math.PI); ctx.fill()
            ctx.restore()
        }
    }

    // Waves on the Reach's shore, as on the southern beach but shorter: it's
    // shingle, and the fjord is sheltered.
    #waves(ctx, pal) {
        const coast = this.world.northCoast
        if (!coast || !coast.length) return
        ctx.save()
        ctx.strokeStyle = pal.foam
        ctx.lineCap = "round"
        const t = this.reduce ? 0 : this.t
        for (let i = 0; i < coast.length - 6; i += 6) {
            const p = coast[i]
            if (!this.#visible(p.x, p.y, 80)) continue
            const q = coast[i + 6], sheltered = p.y < 2560 && p.x > 5560
            for (let k = 0; k < 2; k++) {
                const phase = ((t * 0.16 + i * 0.013 + k * 0.5) % 1)
                const out = (sheltered ? 22 : 48) * (1 - phase) - 3
                ctx.globalAlpha = Math.sin(phase * Math.PI) * (sheltered ? 0.45 : 0.75)
                ctx.lineWidth = sheltered ? 1.4 : 2
                ctx.beginPath()
                ctx.moveTo(p.x + p.nx * out, p.y + p.ny * out)
                ctx.quadraticCurveTo((p.x + q.x) / 2 + p.nx * (out + 5), (p.y + q.y) / 2 + p.ny * (out + 5), q.x + q.nx * out, q.y + q.ny * out)
                ctx.stroke()
            }
        }
        ctx.restore()
    }

    // A humpback: its back breaks the surface, it blows twice, then the
    // flukes rise and it's gone.
    #whale(ctx, pal, w) {
        const k = w.age / w.life
        const dark = pal.night ? "#1A2A33" : "#34454F"
        ctx.save()
        ctx.translate(w.x, w.y)
        ctx.scale(w.dir * 1.5, 1.5)
        // the slick it leaves
        ctx.fillStyle = withAlpha(pal.seaShallow, 0.35)
        ctx.beginPath(); ctx.ellipse(-30, 4, 70, 14, 0, 0, TAU); ctx.fill()
        if (k < 0.72) {
            const rise = Math.sin(Math.min(1, k / 0.72) * Math.PI)
            ctx.fillStyle = dark
            ctx.beginPath(); ctx.ellipse(0, 0, 44 * (0.5 + rise * 0.5), 8 * rise + 1, 0, Math.PI, 0); ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.fillStyle = dark; ctx.beginPath(); ctx.moveTo(12, -6 * rise); ctx.lineTo(18, -12 * rise); ctx.lineTo(22, -5 * rise); ctx.fill()
            // two blows
            ;[0.12, 0.42].forEach((at) => {
                const b = (k - at) / 0.14
                if (b < 0 || b > 1) return
                ctx.fillStyle = withAlpha(pal.snowLight, (1 - b) * 0.8)
                for (let i = 0; i < 6; i++) { ctx.beginPath(); ctx.arc(30 + (i - 2.5) * 4 * b, -10 - b * 40 + (i % 2) * 6, 3 + b * 5, 0, TAU); ctx.fill() }
            })
        } else {
            // the flukes
            const f = (k - 0.72) / 0.28, up = Math.sin(f * Math.PI)
            ctx.fillStyle = dark
            ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(-2, -26 * up); ctx.quadraticCurveTo(-18, -38 * up, -26, -30 * up); ctx.quadraticCurveTo(-10, -30 * up, 0, -24 * up); ctx.quadraticCurveTo(10, -30 * up, 26, -30 * up); ctx.quadraticCurveTo(18, -38 * up, 2, -26 * up); ctx.lineTo(6, 0); ctx.closePath()
            ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.1; ctx.stroke()
            ctx.strokeStyle = withAlpha(pal.foam, 0.8); ctx.lineWidth = 1.4
            ctx.beginPath(); ctx.ellipse(0, 1, 16 + f * 20, 4 + f * 3, 0, 0, TAU); ctx.stroke()
        }
        ctx.restore()
    }

    // --- in the scene (depth-sorted with everything upright) --------------------------

    sceneItems() {
        if (!this.active) return []
        const a = this.world.anchors, items = []
        ;(a.portFlags || []).forEach((f) => { if (this.#visible(f.x, f.y, 100)) items.push({ y: f.y + 0.5, draw: (ctx, pal) => this.#flag(ctx, pal, f) }) })
        this.deer.forEach((d) => { if (this.#visible(d.x, d.y)) items.push({ y: d.y, draw: (ctx, pal) => this.#reindeer(ctx, pal, d) }) })
        return items
    }

    #flag(ctx, pal, f) {
        const t = this.reduce ? 0 : this.t
        const x0 = f.x + 1, y0 = f.y - 86, w = 22, h = 14
        const wave = (u) => Math.sin(u * 5 - t * 7 + f.x * 0.05) * 2.2 * u
        const bands = f.bands || ["#FAF7EF"]
        ctx.save()
        bands.forEach((c, k) => {
            const top = y0 + (k * h) / bands.length, bottom = y0 + ((k + 1) * h) / bands.length
            ctx.beginPath()
            for (let i = 0; i <= 8; i++) { const u = i / 8; ctx.lineTo(x0 + u * w, top + wave(u)) }
            for (let i = 8; i >= 0; i--) { const u = i / 8; ctx.lineTo(x0 + u * w, bottom + wave(u) + 0.4) }
            ctx.closePath(); ctx.fillStyle = c; ctx.fill()
        })
        ctx.beginPath()
        for (let i = 0; i <= 8; i++) { const u = i / 8; ctx.lineTo(x0 + u * w, y0 + wave(u)) }
        for (let i = 8; i >= 0; i--) { const u = i / 8; ctx.lineTo(x0 + u * w, y0 + h + wave(u)) }
        ctx.closePath(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
        ctx.restore()
    }

    #reindeer(ctx, pal, d) {
        const s = d.calf ? 0.9 : 1.3
        const grazing = d.graze > 0
        const bob = this.reduce ? 0 : Math.sin(d.bob) * 0.8
        const coat = pal.night ? "#4E4034" : "#8A6A4A", belly = pal.night ? "#8C8680" : "#E8E0CE"
        ctx.save()
        ctx.translate(d.x, d.y)
        ctx.scale(d.facing * s, s)
        ctx.fillStyle = `rgba(${pal.night ? "0,8,20" : "60,90,130"},0.25)`
        ctx.beginPath(); ctx.ellipse(2, 1, 18, 4, 0, 0, TAU); ctx.fill()
        ;[-10, -5, 6, 11].forEach((x, i) => line(ctx, [[x, -12], [x + (i % 2 ? 1 : -1) * bob * 0.6, 0]], pal.ink, 2.2))
        ctx.beginPath(); ctx.ellipse(0, -16 + bob * 0.3, 16, 8, 0, 0, TAU); ctx.fillStyle = coat; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
        ctx.fillStyle = belly; ctx.beginPath(); ctx.ellipse(-12, -15, 5, 5, 0, 0, TAU); ctx.fill()
        const hx = 16, hy = grazing ? -6 : -24
        line(ctx, [[10, -18], [hx, hy + 4]], coat, 5)
        ctx.beginPath(); ctx.ellipse(hx + 3, hy + 2, 6, 4, grazing ? 0.9 : 0.3, 0, TAU); ctx.fillStyle = coat; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
        if (!d.calf) {
            ctx.strokeStyle = pal.night ? "#8A7A60" : "#C9B48A"; ctx.lineWidth = 1.4; ctx.lineCap = "round"
            ctx.beginPath()
            ctx.moveTo(hx + 1, hy - 1); ctx.quadraticCurveTo(hx - 6, hy - 12, hx - 2, hy - 18); ctx.moveTo(hx - 3, hy - 9); ctx.lineTo(hx - 8, hy - 11)
            ctx.moveTo(hx + 3, hy - 1); ctx.quadraticCurveTo(hx + 8, hy - 12, hx + 5, hy - 19); ctx.moveTo(hx + 6, hy - 9); ctx.lineTo(hx + 11, hy - 12)
            ctx.stroke()
        }
        ctx.restore()
    }

    // --- in the air ------------------------------------------------------------------

    drawAir(ctx, pal, light) {
        if (!this.active) return
        ctx.save()
        const t = this.reduce ? 0 : this.t
        // steam: slow, wide and white; smoke: smaller and greyer
        this.steam.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * (pal.night ? 0.3 : 0.45)
            ctx.fillStyle = pal.night ? "#B8C4C8" : "#FFFFFF"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 20, 0, TAU); ctx.fill()
        })
        this.smoke.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * (pal.night ? 0.22 : 0.3)
            ctx.fillStyle = pal.night ? "#8A969A" : "#ECEAE4"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 16, 0, TAU); ctx.fill()
        })
        ctx.globalAlpha = 1
        // pigeons, and their shadows on the ground
        const loft = this.world.anchors.loft
        if (loft && this.#visible(loft.x, loft.y, 700)) {
            this.pigeons.forEach((p) => {
                const away = p.away > 0 ? Math.sin((p.away / 9) * Math.PI) * 520 : 0
                const x = loft.x + Math.cos(p.a) * p.rad + Math.cos(p.dir) * away
                const y = loft.y + Math.sin(p.a) * p.rad * 0.45 - p.h + Math.sin(p.dir) * away * 0.6
                ctx.fillStyle = `rgba(${pal.shadow},0.14)`
                ctx.beginPath(); ctx.ellipse(x + 30, y + p.h + 120, 5, 2, 0, 0, TAU); ctx.fill()
                const flap = Math.sin(p.phase) * 4
                ctx.strokeStyle = pal.night ? "#9098A8" : "#6E7482"; ctx.lineWidth = 1.8; ctx.lineCap = "round"
                ctx.beginPath(); ctx.moveTo(x - 6, y - flap); ctx.quadraticCurveTo(x - 2, y - 2, x, y); ctx.quadraticCurveTo(x + 2, y - 2, x + 6, y - flap); ctx.stroke()
            })
        }
        // snow falling over the Reach: flakes placed across the view, fading
        // in east of the old map's edge
        const v = this.view, start = this.world.mainlandWidth || 4000
        if (v.x + v.w > start) {
            ctx.fillStyle = pal.night ? "#DCE6EA" : "#FFFFFF"
            const drift = Math.sin(t * 0.3) * 14
            this.flakes.forEach((f) => {
                const x = v.x + ((f.u * v.w + drift * t * 0.1 + Math.sin(t * 0.8 + f.sway) * 12) % v.w + v.w) % v.w
                const y = v.y + ((f.v * v.h + t * f.speed) % v.h + v.h) % v.h
                const fade = Math.min(1, Math.max(0, (x - start) / 500))
                if (fade <= 0) return
                ctx.globalAlpha = fade * (pal.night ? 0.55 : 0.8)
                ctx.beginPath(); ctx.arc(x, y, f.s, 0, TAU); ctx.fill()
            })
        }
        ctx.restore()
    }

    // --- light, over the hour's tint -------------------------------------------------

    drawLights(ctx, pal, light) {
        if (!this.active) return
        const a = this.world.anchors
        const dark = pal.night || light.lamps >= 0.7
        ctx.save()
        ctx.globalCompositeOperation = "lighter"
        if (dark) this.#aurora(ctx, light)
        if (a.vaultGlow && dark && this.#visible(a.vaultGlow.x, a.vaultGlow.y, 200)) {
            const t = this.reduce ? 0 : this.t
            ctx.globalAlpha = 0.5 + 0.3 * Math.sin(t * 1.3)
            ctx.drawImage(this.glow, a.vaultGlow.x - 50, a.vaultGlow.y - 70, 100, 140)
        }
        ctx.restore()
    }

    // The northern lights: curtains of green hung across the Reach's sky,
    // folding slowly, pink at their hems, brightest over Aurora point.
    #aurora(ctx, light) {
        const v = this.view
        const x0 = 4700, x1 = this.world.W
        if (v.x + v.w < x0 || v.y > 2400) return
        const t = this.reduce ? 0 : this.t
        const bands = [{ y: 560, amp: 170, h: 320, speed: 0.07, a: 0.32 }, { y: 1050, amp: 150, h: 260, speed: 0.05, a: 0.22 }]
        const focus = this.world.anchors.auroraPoint
        bands.forEach((b, bi) => {
            for (let x = Math.max(x0, v.x - 60); x < Math.min(x1, v.x + v.w + 60); x += 18) {
                const y = b.y + Math.sin(x / 520 + t * b.speed * 6 + bi) * b.amp + Math.sin(x / 190 - t * b.speed * 11) * 30
                if (y - b.h > v.y + v.h || y < v.y - 40) continue
                const shimmer = 0.55 + 0.45 * Math.sin(x / 60 + t * 1.6 + bi * 2) * Math.sin(x / 230 - t * 0.7)
                const near = focus ? Math.max(0, 1 - Math.hypot(x - focus.x, y - focus.y) / 1800) : 0
                const edge = Math.min(1, (x - x0) / 600)
                ctx.globalAlpha = Math.max(0, b.a * shimmer * edge * (0.6 + near * 0.8) * light.lamps)
                ctx.drawImage(this.curtain, x - 30, y - b.h, 60, b.h)
            }
        })
    }
}

// One strip of aurora: green, fading upward, with a pink hem.
function makeCurtain() {
    const c = document.createElement("canvas")
    c.width = 16; c.height = 128
    const g = c.getContext("2d")
    const grad = g.createLinearGradient(0, 128, 0, 0)
    grad.addColorStop(0, "rgba(255,120,190,0)")
    grad.addColorStop(0.05, "rgba(255,130,200,0.3)")
    grad.addColorStop(0.14, "rgba(120,255,190,0.9)")
    grad.addColorStop(0.5, "rgba(90,230,170,0.4)")
    grad.addColorStop(1, "rgba(80,160,255,0)")
    g.fillStyle = grad; g.fillRect(0, 0, 16, 128)
    // soften the sides
    const side = g.createLinearGradient(0, 0, 16, 0)
    side.addColorStop(0, "rgba(0,0,0,1)"); side.addColorStop(0.5, "rgba(0,0,0,0)"); side.addColorStop(1, "rgba(0,0,0,1)")
    g.globalCompositeOperation = "destination-out"
    g.fillStyle = side; g.fillRect(0, 0, 16, 128)
    return c
}
